import React, { useState, useEffect, useContext, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import GroupedLLMSelector from "../Dashboard/Component/GroupedLLMSelector";
import {
  fetchCustomerLlms,
  getLlms,
  getCustomerMetrcisAlertsData,
  getAllBrands,
  getGeographies,
} from "../../services/apiService";
import DateRangePicker from "../Dashboard/Component/DateRangePicker";
import { AuthContext } from "../../context/auth-context";
import SummaryCard from "./SummaryCard/SummaryCard";
import { Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { fetchBrands } from "../../redux/actions/brandsActions";
import { fetchBrandProducts } from "../../redux/actions/brandProductsActions";
import EmptyView from "../../Component/EmptyView/EmptyView";
import FilterSelection from "../Dashboard/common/FilterSelection";

export const getDefaultCase = (persona, geography, market) => {
  const category = [persona, geography, market].filter(Boolean).join(" | ");
  return (
    <p
      style={{
        fontSize: "16px",
        fontWeight: "700",
        marginTop: '0px'
      }}
    >
      {category}
    </p>
  );
};

const HomePage = () => {
  const [selectedLlmItems, setSelectedLlmItems] = useState([]);
  const [selectedLlmProviders, setSelectedLlmProviders] = useState([]);
  const [selectedSubFrequencyItems, setSelectedSubFrequencyItems] =
    useState("Current");
  const [dbLlms, setDbLlms] = useState([]);
  const [customerLlm, setCustomerLlm] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metricsDataWithAlerts, setMetricsDataWithAlerts] = useState([]);
  const [llmDomains, setLlmDomains] = useState();
  const [geography, setGeography] = useState("");
  const [filters, setFilters] = useState({
    brand: [],
    product: [],
    persona: [],
    market: [],
    geo: [],
    category: [],
  });


  const dispatch = useDispatch();

  const handleLlmCheckChange = (selected) => {
    const selectedArray = Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : [];

    const selectedProviderValues = selectedArray.map(
      (option) => option?.value
    );

    // Extract all LLM names from selected providers
    const allLlmNames = [];
    selectedArray.forEach((providerOption) => {
      providerOption.llms?.forEach((llm) => {
        allLlmNames.push(llm.name);
      });
    });

    setSelectedLlmProviders(selectedProviderValues);
    setSelectedLlmItems(allLlmNames);
  };

  function prepareSelectOptions(dataArray) {
    const options = {
      brand: new Set(),
      product: new Set(),
      persona: new Set(),
      market: new Set(),
      geo: new Set(),
      category: new Set(),
    };

    dataArray?.forEach((item) => {
      if (item?.brandName) options.brand.add(item.brandName);
      if (item?.personaName) options.persona.add(item.personaName);
      if (item?.marketName) options.market.add(item.marketName);
      if (item?.geoName) options.geo.add(item.geoName);
      if (item?.categoryName) options.category.add(item.categoryName);

      if (Array.isArray(item?.products)) {
        item.products.forEach((subitem) => {
          if (subitem?.productName) options.product.add(subitem.productName);
          if (subitem?.personaName) options.persona.add(subitem.personaName);
          if (subitem?.marketName) options.market.add(subitem.marketName);
          if (subitem?.geoName) options.geo.add(subitem.geoName);
          if (subitem?.categoryName) options.category.add(subitem.categoryName);
        });
      }
    });

    return Object.fromEntries(
      Object.entries(options).map(([key, values]) => [
        key,
        [...values].map((value) => ({ value, label: value })),
      ])
    );
  }




  // const handleFilterChange = (key, selectedOptions) => {
  //   if ((key !== 'brand' && key !== 'product') && (filters?.brand.length === 0 && filters?.product.length === 0)) {
  //     toast.warn('Please select a brand or product first!', {
  //       autoClose: 1000,
  //     });
  //     return;
  //   }

  //   setFilters((prev) => ({
  //     ...prev,
  //     [key]: selectedOptions.map((option) => option.value),
  //   }));
  // };

  const handleFilterChange = (key, selectedOptions) => {
    if ((key !== 'brand' && key !== 'product') && (filters?.brand.length === 0 && filters?.product.length === 0)) {
      toast.warn('Please select a brand or product first!', {
        autoClose: 1000,
      });
      return;
    }

    const updatedFilters = {
      ...filters,
      [key]: selectedOptions.map((option) => option.value),
    };

    setFilters(updatedFilters);
    sessionStorage.setItem('homePageFilters', JSON.stringify(updatedFilters));
  };

  const optionsMapping = useMemo(() => {
    if (!metricsDataWithAlerts || metricsDataWithAlerts.length === 0) {
      return {}; // or return undefined/null based on how you use it
    }
    return prepareSelectOptions(metricsDataWithAlerts);
  }, [metricsDataWithAlerts]);



  useEffect(() => {
    if (!optionsMapping || !Object.keys(optionsMapping).length) return;

    const savedFilters = sessionStorage.getItem('homePageFilters');
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      setFilters(parsedFilters); // Use saved filters
      return
    }

    const prePopulatedFilters = {
      brand: [],
      product: [],
      category: [],
      persona: [],
      market: [],
      geo: [],
    };

    prePopulatedFilters.brand = [optionsMapping?.brand[0]?.value]
    // 2. Category – all
    prePopulatedFilters.category = optionsMapping?.category?.map(opt => opt.value);

    // 3. Persona – General Persona if available, else first
    const generalPersona = optionsMapping?.persona?.find(p => p.value === "General Persona");
    if (generalPersona) {
      prePopulatedFilters.persona = [generalPersona?.value];
    } else if (optionsMapping.persona.length > 0) {
      prePopulatedFilters.persona = [optionsMapping?.persona[0]?.value];
    }

    // 4. Market – General Market if available, else first
    const generalMarket = optionsMapping.market.find(m => m.value === "General Market");
    if (generalMarket) {
      prePopulatedFilters.market = [generalMarket?.value];
    } else if (optionsMapping.market.length > 0) {
      prePopulatedFilters.market = [optionsMapping?.market[0]?.value];
    }

    // 5. Geo – any one (first)
    if (optionsMapping.geo.length > 0) {
      prePopulatedFilters.geo = [optionsMapping?.geo[0]?.value];
    }

    setFilters(prePopulatedFilters);
    sessionStorage.setItem('homePageFilters', JSON.stringify(prePopulatedFilters));
  }, [optionsMapping]);

  const filteredData = useMemo(() => {
    return metricsDataWithAlerts?.flatMap((brandItem) => {
      const {
        brandName,
        personaName,
        marketName,
        geoName,
        brandId,
        categoryName,
        brandDomain,
        products = [],
      } = brandItem;

      const brandMatch =
        (!filters.brand.length || filters.brand.includes(brandName)) &&
        (!filters.persona.length || filters.persona.includes(personaName)) &&
        (!filters.market.length || filters.market.includes(marketName)) &&
        (!filters.geo.length || filters.geo.includes(geoName)) &&
        (!filters.category.length || filters.category.includes(categoryName));

      const productMatchList = products.filter((product) => {
        return (
          (!filters.product.length || filters.product.includes(product.productName)) &&
          (!filters.persona.length || filters.persona.includes(product.personaName)) &&
          (!filters.market.length || filters.market.includes(product.marketName)) &&
          (!filters.geo.length || filters.geo.includes(product.geoName)) &&
          (!filters.category.length || filters.category.includes(product.categoryName))
        );
      });

      const isBrandSelected = filters.brand.length > 0;
      const isProductSelected = filters.product.length > 0;

      // ---- CASE 1: Only brand selected (ignore products completely)
      if (isBrandSelected && !isProductSelected) {
        return brandMatch ? [{ ...brandItem, products: [] }] : [];
      }

      // ---- CASE 2: Only product selected (ignore brand filters)
      if (!isBrandSelected && isProductSelected) {
        return productMatchList.length > 0
          ? [{ brandDomain, brandId, products: productMatchList }]
          : [];
      }

      // ---- CASE 3: Both brand and product selected (apply both)
      if (isBrandSelected && isProductSelected) {
        return brandMatch
          ? [{ ...brandItem, products: productMatchList }]
          : [{ brandDomain, brandId, products: productMatchList }];
      }

      // ---- CASE 4: No filters applied - return everything
      return brandItem;
    });
  }, [metricsDataWithAlerts, filters]);

  const { user } = useContext(AuthContext);

  const customerId = user?.customer_id || 0;
  const customerName = user?.customer?.name;
  const isCompanyUser = user?.user_role?.role?.name === "Company user"

  useEffect(() => {
    dispatch(fetchBrands(customerId));
    dispatch(fetchBrandProducts(customerId));
  }, []);

  useEffect(() => {
    async function fetchData() {
      const allBrands = await getAllBrands();
      const { llms: allLlms } = allBrands?.data || {};

      const createDomainMap = (data, key = "domain_url") =>
        data?.reduce((acc, item) => {
          acc[item.name] = item[key];
          return acc;
        }, {});

      setLlmDomains(createDomainMap(allLlms));

      const llms = await getLlms();
      let customerSpecificLlms = await fetchCustomerLlms([
        Number(customerId),
      ]);
      // customerSpecificLlms = customerSpecificLlms?.data?.filter(obj => obj.llm_monitoring);
      customerSpecificLlms = customerSpecificLlms?.data
      const llmIds = new Set(customerSpecificLlms?.map(obj => obj.llm_id));

      // Build a map of llm_id -> llm_provider from customerSpecificLlms
      const providerMap = {};
      customerSpecificLlms?.forEach((item) => {
        if (!providerMap[item.llm_id]) {
          providerMap[item.llm_id] = item?.llm?.llm_provider;
        }
      });

      console.log("Provider Map:", providerMap, customerSpecificLlms);

      // Merge llm_provider into filtered global LLMs
      const filtered = llms?.data
        ?.filter(obj => llmIds.has(obj.id))
        ?.map(obj => ({
          ...obj,
          llm_provider: providerMap[obj.id] || "Other",
        }));

      setDbLlms(filtered);
      setCustomerLlm(customerSpecificLlms);

      // Initialize with all providers selected
      const allProviders = [...new Set(filtered?.map(obj => obj.llm_provider))];
      setSelectedLlmProviders(allProviders);
      setSelectedLlmItems(filtered?.map((obj) => obj?.name));
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedLlmItems?.length && selectedSubFrequencyItems) {
      setLoading(true);
      handleBrandIndexData();
    }
  }, [selectedLlmItems, selectedSubFrequencyItems]);



  const handleBrandIndexData = async () => {
    const llmIds = dbLlms
      .filter((item) => selectedLlmItems.includes(item?.name))
      .map((obj) => obj?.id);
    const geoId = customerName && customerName === "RGE" ? 11 : 1;
    let geo = await getGeographies();
    geo = geo?.data?.filter((obj) => obj.id === geoId);

    setGeography(geo?.[0]?.name);

    const customerLlmIds = customerLlm
      .filter(
        (item) =>
          llmIds.includes(item?.llm_id)
      )
      .map((obj) => obj?.id);

    const payload = {
      customerId,
      llmIds: customerLlmIds,
      timePeriod: selectedSubFrequencyItems,
      isCompanyUser

    };

    try {
      const apiResp = await getCustomerMetrcisAlertsData(payload);
      setMetricsDataWithAlerts(apiResp?.customerMetrcisDataWithAlerts);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err, {
        autoClose: 700,
      });
    }
  };

  function deduplicateProductsByKeys(dataArray) {
    const brandSeen = new Set();

    return dataArray.map((brandItem) => {
      const { brandId, products } = brandItem;

      if (!Array.isArray(products) || products.length === 0) {
        return brandItem;
      }

      if (!brandSeen.has(brandId) && Array.isArray(products) && products.length > 0) {
        brandSeen.add(brandId);
        return brandItem; // keep products as-is for the first occurrence
      }

      // For duplicate brandName, empty the products array
      return {
        ...brandItem,
        products: [],
      };
    });
  }


  const handlesubFrequencyCheckChange = (key) => {
    setSelectedSubFrequencyItems(key);
  };

  return (
    <>
      <div class="bodycontent">
        <div style={{ marginBottom: "-15px", display: "flex" }}>
          <div class="col-md-3">
            <div className="input-group input-group-lg mb-3">
              {/* <span className="input-group-text" id="basic-addon1">
                <i className="feather icon-target"></i>
              </span> */}
              <GroupedLLMSelector
                selectedProviders={selectedLlmProviders}
                handleChange={handleLlmCheckChange}
                options={dbLlms}
                allowMultipleProviders={true}
              />
            </div>
          </div>

          {/* &nbsp; &nbsp; &nbsp;
          <DateRangePicker
            activeTarget={selectedSubFrequencyItems}
            handleChange={handlesubFrequencyCheckChange}
            isHomePage={true}
          /> */}
          &nbsp; &nbsp; &nbsp;

        </div>
        <br />
        <hr />

        <div style={{ display: "flex", width: '95%' }}>
          {["brand", "product", 'category', 'persona', 'market', 'geo',].map((filterKey) => (
            <>
              <div className="col-md-2" key={filterKey}>
                <FilterSelection
                  options={optionsMapping[filterKey]}
                  type={filterKey}
                  selectedOptions={filters[filterKey].map((value) => ({
                    value,
                    label: value,
                  }))}
                  handleChange={(selectedOptions) =>
                    handleFilterChange(filterKey, selectedOptions)
                  }
                />
              </div>{" "}
              &nbsp;&nbsp;&nbsp;
            </>
          ))}

        </div>



        <div class="col-md-12 mt-4">
          <div class="card p-3 custCard">
            {loading ? (
              <div
                style={{
                  fontSize: "19px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "20px",
                  color: "green",
                }}
              >
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                Please Wait...
              </div>
            ) : !metricsDataWithAlerts || metricsDataWithAlerts.length === 0 ? (
              <EmptyView message="Nothing here!" />
            ) : (
              <>
                {/* {getDefaultCase("General Persona", geography, "General Market")}
                <hr /> */}
                {deduplicateProductsByKeys(filteredData)?.map((data) => {
                  const hasProducts = data?.products?.length > 0;
                  const hasBrand = data?.brandName;

                  return (
                    <>
                      {hasBrand && (
                        <><SummaryCard
                          frequency={selectedSubFrequencyItems}
                          domainUrl={data?.brandDomain}
                          data={data}
                          llmDomains={llmDomains}
                          dbLlms={dbLlms}
                        /> <hr />
                        </>

                      )}

                      {hasProducts &&
                        data?.products?.map((productData) => (
                          <>
                            <SummaryCard
                              key={productData.productName}
                              frequency={selectedSubFrequencyItems}
                              domainUrl={data?.brandDomain}
                              isProduct={true}
                              data={productData}
                              llmDomains={llmDomains}
                              dbLlms={dbLlms}
                            />
                            <hr />
                          </>
                        ))}
                    </>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>

      <ToastContainer />
    </>
  );
};

export default HomePage;
